import {
  FieldWrapper,
  InputSelectionList,
  InputSelectionListProps,
  SelectionListChangeEvent,
  Tag,
  Text,
} from '@dixa/lib-shared-habla';
import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'lib-shared-hook-form';
import type { Tag as TagT } from 'lib-shared-types-api';
import { useCallback, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useDebounce } from 'react-use';

import { CategorizedTagsList } from 'components/categorizedTagsList';

import styles from './hookFormCategorizedTagsSelect.module.scss';

type HookFormCategorizedTagsSelectProps = Partial<
  Omit<InputSelectionListProps<TagT>, 'children'>
>;

export const HookFormCategorizedTagsSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  allProps: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    HookFormCategorizedTagsSelectProps
  >,
): JSX.Element => {
  const { componentProps = {}, ...props } = allProps;
  const { controller, fieldWrapperProps } = useHookFormFieldWrapper(allProps);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const selectedTags: readonly TagT[] = controller.field.value ?? [];

  const handleChange = useCallback(
    ({ selectedValues }: SelectionListChangeEvent<TagT>) => {
      controller.field.onChange(selectedValues);
    },
    [controller.field],
  );

  const handleToggle = useCallback(
    (newIsOpen: boolean) => {
      setIsOpen(newIsOpen);
      if (!newIsOpen) {
        controller.field.onBlur();
        setSearchTerm('');
      }
    },
    [controller.field],
  );

  return (
    <FieldWrapper {...fieldWrapperProps}>
      <InputSelectionList
        multiple
        keepOpen
        placeholder="Select tags"
        equalityFn={(a: TagT, b: TagT) => a.id === b.id}
        getItemTitleByValue={(tag: TagT | null | undefined) =>
          tag ? (
            <Tag color={tag.color} noBorder className={styles.tag}>
              {tag.name}
            </Tag>
          ) : (
            <Text.Paragraph>Unknown</Text.Paragraph>
          )
        }
        {...componentProps}
        value={selectedTags as TagT[]}
        status={FieldWrapper.getStatus(fieldWrapperProps.meta)}
        disabled={props.disabled}
        filter={{
          name: props.name,
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
        }}
        onToggleDropdown={handleToggle}
        slotBeforeList={
          <CategorizedTagsList
            selectedTags={selectedTags}
            isActive={isOpen}
            showSearch={false}
            searchTerm={debouncedSearchTerm}
            width="100%"
            onChange={handleChange}
          />
        }
      />
    </FieldWrapper>
  );
};
